<template>
  <div>
    <player-seasons-table
      v-if="isSeasonsTableVisible"
      :playerId="playerId"
      :seasons="seasons"
      :seasonTypeNameMap="seasonTypeNameMap"
      @season-finish-requested="finishSeasonWithConfirmation"
    />

    <p v-if="message !== null">
      {{message}}
    </p>

    <loading-indicator v-if="isLoading" />
  </div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';

import { SEASON_STATUS_FINISHED              } from '../model/season.js';
import { convertSeasonTypeArrayToTypeNameMap } from '../model/season.js';
import { getSeasonTypeNameFromMap            } from '../model/season.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import LoadingIndicator   from './LoadingIndicator.vue';
import PlayerSeasonsTable from './PlayerSeasonsTable.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'PlayerSeasons',

  components: {
    LoadingIndicator,
    PlayerSeasonsTable,
  },

  props: {
    playerId: {
      type: Number,
      required: true,
    }
  },

  data() {
    return {
      loadingStatus: LOADING_STATUS__INIT,
      seasonTypeNameMap: null,
      seasons: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(!this.isLoadedNormal) {
        return null;
      }

      const seasons = this.seasons;
      if(seasons !== null && seasons.length === 0) {
        return 'У этого игрока нет сезонов.';
      }

      return null;
    },

    /** @returns {boolean} */
    isSeasonsTableVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      const seasons = this.seasons;
      if(seasons === null) {
        return false;
      }

      return seasons.length > 0;
    },
  },

  created() {
    this.loadPlayerSeasons();
  },

  methods: {
    loadPlayerSeasons() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = '/player/' + this.playerId + '/seasons';
      HTTP.get(url)
      .then(response => {
        const data = response.data.data;

        this.seasonTypeNameMap = convertSeasonTypeArrayToTypeNameMap(data.seasonTypes);
        
        const seasons = data.seasons;
        seasons.sort((a, b) => b.stime - a.stime);

        this.seasons = seasons
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Finish
    //-------------------------------------------------------------------------

    finishSeasonWithConfirmation(season) {
      notifier.deleteSenderNotifications(this);

      this.confirmSeasonFinish(
        season,
        () => {
          this.finishSeason(season);
        },
      );
    },

    /**
     * @param {number} seasonId
     * @param {ConfirmHandle} onConfirmed
     */
    confirmSeasonFinish(season, onConfirmed) {
      const seasonId = season.seasonId;
      const seasonTypeName = this.getSeasonTypeName(season.seasonProtoId);
      const playerId = this.playerId;

      const messageTemplate = `Are you sure you want to finish season ${seasonId} (${seasonTypeName}) for player ${playerId} {env}?`;

      confirmAction(
        this,
        messageTemplate,
        'Finish',
        onConfirmed,
      );
    },

    getSeasonTypeName(seasonProtoId) {
      return getSeasonTypeNameFromMap(this.seasonTypeNameMap, seasonProtoId);
    },

    finishSeason(season) {
      const seasonId = season.seasonId;
      const playerId = this.playerId;
      const url = `/season/${seasonId}/player/${playerId}/finalize`;

      HTTP.post(url)
      .then(() => {
        season.status = SEASON_STATUS_FINISHED;
        notifier.showSuccess('Season finished for this player.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
  },
}
</script>
